import { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import InputField from '@components/inputs/input';
import Select from '@components/select';
import Link from '@components/link';
import Button from '@components/button';
import CalculatorLayout from '../shared/calculator-layout';
import DebtToIncomeSlider from '../shared/debt-to-income-slider';

import {
  calculateAffordability,
  calculatePaymentBreakdown,
  formatCurrency
} from '../shared/calculator-utils';
import useInterestRate from '../shared/use-interest-rate';
import useLoanTerm from '../shared/use-loan-term';
import {
  LOAN_TERM_OPTIONS,
  CTA_BUTTON_CONFIG,
  DISCLAIMER_URLS
} from '../shared/calculator-ui-constants';

import styles from './styles.module.scss';
const {
  calculator_cta,
  results_header,
  price_display,
  price_amount,
  price_label,
  stats_grid,
  stat_item,
  stat_label,
  stat_value,
  cta_wrapper,
  select_wrapper,
  condensed: condensedClass
} = styles;

const AffordabilityCalculator = ({
  calculatorOptions = [],
  onCalculatorChange,
  showCalculatorSelector = true,
  currentRates,
  hideVisual = false,
  condensed = false,
  title,
  description
}) => {
  const [monthlyIncome, setMonthlyIncome] = useState(8000);
  const [monthlyDebts, setMonthlyDebts] = useState(500);
  const [downPayment, setDownPayment] = useState(100000);
  const [targetDTI, setTargetDTI] = useState(0.36);

  // Use custom hooks for rate and term management
  const { interestRate, handleInterestRateChange, setInterestRate } = useInterestRate(
    currentRates?.[30] || 6.42
  );

  const { loanTerm, handleLoanTermChange } = useLoanTerm(30, currentRates, setInterestRate);

  const affordability = calculateAffordability(
    monthlyIncome,
    monthlyDebts,
    downPayment,
    interestRate,
    loanTerm,
    targetDTI
  );

  // Ensure down payment meets 5% minimum for conventional loans
  const minDownPayment = affordability.maxPurchasePrice * 0.05;
  const effectiveDownPayment = Math.max(downPayment, minDownPayment);

  // When no loan is affordable (maxLoanAmount is 0), show $0 payment
  let paymentBreakdown;
  if (affordability.maxLoanAmount === 0) {
    paymentBreakdown = {
      principalAndInterest: 0,
      taxes: 0,
      insurance: 0,
      mortgageInsurance: 0,
      total: 0
    };
  } else {
    const estimatedTaxes = (affordability.maxPurchasePrice * 0.012) / 12;
    const estimatedInsurance = (affordability.maxPurchasePrice * 0.005) / 12;

    // Ensure loan amount never goes negative (can happen when down payment exceeds max price)
    const loanAmount = Math.max(0, affordability.maxPurchasePrice - effectiveDownPayment);

    paymentBreakdown = calculatePaymentBreakdown(
      loanAmount,
      interestRate,
      loanTerm,
      affordability.maxPurchasePrice,
      estimatedTaxes,
      estimatedInsurance
    );
  }

  const disclaimerContent = (
    <>
      *The affordability calculation is based on conventional lending guidelines and debt-to-income
      ratios.
      <br />
      Actual loan approval depends on credit score, employment history, and other factors.{' '}
      <Link url={DISCLAIMER_URLS.findLoanOfficer}>Contact a loan officer</Link> for personalized
      pre-approval.
    </>
  );

  const resultsContent = (
    <>
      <h3 className={results_header}>You could afford</h3>

      <div className={price_display}>
        <div className={price_amount}>{formatCurrency(affordability.maxPurchasePrice)}</div>
        <div className={price_label}>Maximum home price</div>
      </div>

      <div
        className={classNames(stats_grid, {
          [condensedClass]: condensed
        })}
      >
        <div className={stat_item}>
          <div className={stat_label}>MONTHLY PAYMENT</div>
          <div className={stat_value}>{formatCurrency(paymentBreakdown.total)}</div>
        </div>
        <div className={stat_item}>
          <div className={stat_label}>DOWN PAYMENT</div>
          <div className={stat_value}>{formatCurrency(effectiveDownPayment)}</div>
        </div>
      </div>

      <DebtToIncomeSlider
        value={targetDTI}
        readOnly={false}
        onChange={(newDTI) => setTargetDTI(newDTI)}
        condensed={condensed}
      />

      <div className={cta_wrapper}>
        <Button
          type="button"
          label={CTA_BUTTON_CONFIG.label}
          url={CTA_BUTTON_CONFIG.url}
          theme={CTA_BUTTON_CONFIG.theme}
          className={calculator_cta}
        />
      </div>
    </>
  );

  return (
    <CalculatorLayout
      title={title || 'Affordability Calculator'}
      description={description || 'Determine how much house you can afford'}
      calculatorType="affordability"
      calculatorOptions={calculatorOptions}
      onCalculatorChange={onCalculatorChange}
      showCalculatorSelector={showCalculatorSelector}
      hideVisual={hideVisual}
      condensed={condensed}
      resultsContent={resultsContent}
      disclaimerContent={disclaimerContent}
    >
      <InputField
        label="Monthly Income"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={monthlyIncome.toString()}
        defaultOnChange={(value) => setMonthlyIncome(parseFloat(value) || 0)}
        defaultOnBlur={(value) => {
          // Only cap debts when user finishes editing income (not on every keystroke)
          const numValue = parseFloat(value) || 0;
          if (monthlyDebts > numValue) {
            setMonthlyDebts(numValue);
          }
        }}
        inputName="monthlyIncome"
        tooltip="Enter your gross monthly income (before taxes and deductions). This is what lenders use to calculate your debt-to-income ratio."
      />

      <InputField
        label="Monthly Debt Payments"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={monthlyDebts.toString()}
        defaultOnChange={(value) => setMonthlyDebts(parseFloat(value) || 0)}
        defaultOnBlur={(value) => {
          // Cap monthly debts at 100% of income when user finishes editing
          const numValue = parseFloat(value) || 0;
          setMonthlyDebts(Math.min(numValue, monthlyIncome));
        }}
        inputName="monthlyDebts"
        tooltip="Include credit cards, auto loans, student loans, and other monthly debt obligations. Do not include utilities or rent."
      />

      <InputField
        label="Down Payment"
        inputType="text"
        inputFormat="currency"
        defaultInputValue={downPayment.toString()}
        defaultOnChange={(value) => setDownPayment(parseFloat(value) || 0)}
        inputName="downPayment"
      />

      <InputField
        label="Interest Rate"
        inputType="text"
        inputFormat="percentage"
        defaultInputValue={interestRate.toString()}
        defaultOnChange={handleInterestRateChange}
        inputName="interestRate"
      />

      <div className={select_wrapper}>
        <Select
          id="loanTerm"
          label="Loan Term"
          options={LOAN_TERM_OPTIONS}
          selected={loanTerm}
          onChange={handleLoanTermChange}
        />
      </div>
    </CalculatorLayout>
  );
};

AffordabilityCalculator.propTypes = {
  calculatorOptions: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired
    })
  ),
  onCalculatorChange: PropTypes.func,
  showCalculatorSelector: PropTypes.bool,
  currentRates: PropTypes.shape({
    15: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    30: PropTypes.oneOfType([PropTypes.string, PropTypes.number])
  }),
  hideVisual: PropTypes.bool,
  condensed: PropTypes.bool,
  title: PropTypes.string,
  description: PropTypes.string
};

export default AffordabilityCalculator;
